import React, { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Jellyfish from '@/effects/animals/Jellyfish';

/**
 * EndingSection - 品牌故事结尾（粒子 + 水母 + 男孩猫 + 灯塔）
 */

/* ───────── Canvas 粒子 ───────── */
const ParticlesCanvas = () => {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    let w, h, id;
    const dots = Array.from({ length: 60 }, () => ({
      x: Math.random(), y: Math.random(), r: Math.random() * 2 + 0.4,
      vy: -(Math.random() * 0.0004 + 0.00015),
      vx: (Math.random() - 0.5) * 0.0001,
      o: Math.random() * 0.6 + 0.2,
      p: Math.random() * 6.28,
    }));
    const resize = () => { w = c.width = c.offsetWidth; h = c.height = c.offsetHeight; };
    resize();
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      dots.forEach(d => {
        d.y += d.vy; d.x += d.vx + Math.sin(d.p) * 0.00008; d.p += 0.008;
        if (d.y < 0) { d.y = 1; d.x = Math.random(); }
        const a = d.o * (0.5 + 0.5 * Math.sin(d.p));
        ctx.beginPath(); ctx.arc(d.x * w, d.y * h, d.r, 0, 6.28);
        ctx.fillStyle = `rgba(145,225,255,${a})`; ctx.shadowColor = 'rgba(88,183,232,0.5)'; ctx.shadowBlur = 5; ctx.fill(); ctx.shadowBlur = 0;
      });
      id = requestAnimationFrame(draw);
    };
    draw();
    window.addEventListener('resize', resize);
    return () => { cancelAnimationFrame(id); window.removeEventListener('resize', resize); };
  }, []);
  return <canvas ref={ref} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 1 }} />;
};

const EndingSection = () => {
  const navigate = useNavigate();

  return (
    <section className="relative" style={{
      background: 'linear-gradient(180deg, rgba(5,13,24,0.0) 0%, rgba(8,18,35,0.5) 30%, rgba(6,14,28,0.8) 70%, #020810 100%)',
      minHeight: '85vh',
      overflow: 'hidden',
    }}>

      {/* 粒子层 */}
      <ParticlesCanvas />

      {/* 水母群 — Apple 黄金比例布局（5 只，分布于结尾画面上部）*/}
      <div style={{ position: 'absolute', inset: 0, zIndex: 2, pointerEvents: 'none', overflow: 'hidden' }}>
        {/* 主焦点 — 黄金分割位 */}
        <div style={{ position: 'absolute', top: '22%', left: '12%' }}>
          <Jellyfish opacity={0.7} size={110} delay={0} duration={11} />
        </div>
        {/* 右上纵深辅助 */}
        <div style={{ position: 'absolute', top: '12%', right: '14%' }}>
          <Jellyfish opacity={0.52} size={85} delay={2} duration={13} />
        </div>
        {/* 中左平衡 */}
        <div style={{ position: 'absolute', top: '48%', left: '6%' }}>
          <Jellyfish opacity={0.45} size={72} delay={5} duration={12} />
        </div>
        {/* 右侧中部点缀 */}
        <div style={{ position: 'absolute', top: '35%', right: '28%' }}>
          <Jellyfish opacity={0.4} size={65} delay={3} duration={14} />
        </div>
        {/* 底部远景浅虚 */}
        <div style={{ position: 'absolute', top: '68%', right: '38%' }}>
          <Jellyfish opacity={0.3} size={55} delay={7} duration={15} />
        </div>
        {/* 左侧底部小点缀 */}
        <div style={{ position: 'absolute', top: '80%', left: '22%' }}>
          <Jellyfish opacity={0.25} size={45} delay={4} duration={16} />
        </div>
        {/* 中间漂浮辅助 */}
        <div style={{ position: 'absolute', top: '55%', left: '45%' }}>
          <Jellyfish opacity={0.28} size={50} delay={9} duration={14} />
        </div>
      </div>

      {/* 内容区域 */}
      <div className="relative z-10 text-center px-6 pt-24 pb-10 max-w-2xl mx-auto">

        {/* 灯塔微光装饰 */}
        <div
          className="absolute top-0 right-1/4 w-64 h-64 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at center, rgba(255,220,140,0.06) 0%, transparent 70%)',
            filter: 'blur(40px)',
          }}
        />

        {/* 核心文案 — 发光 */}
        <h2
          className="text-3xl md:text-4xl lg:text-5xl font-light leading-tight mb-6"
          style={{
            fontFamily: "'Playfair Display', 'Noto Serif SC', Georgia, serif",
            color: 'rgba(238,243,247,0.92)',
            letterSpacing: '-0.01em',
            textShadow: '0 0 40px rgba(88,183,232,0.2), 0 0 80px rgba(88,183,232,0.08)',
            animation: 'endingBreathe 6s ease-in-out infinite',
          }}
        >
          我们终会遇见
        </h2>

        <p
          className="text-base md:text-lg mb-10 font-light"
          style={{ color: 'rgba(158,200,224,0.5)', letterSpacing: '0.02em' }}
        >
          记录下一段感动
        </p>

        {/* 行动按钮 — 纯白无黄色 */}
        <button
          onClick={() => navigate('/diaries')}
          className="group relative px-10 py-4 rounded-full text-sm font-medium transition-all duration-500"
          style={{
            background: 'rgba(15,30,55,0.5)',
            color: '#ffffff',
            border: '1px solid rgba(130,200,255,0.2)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            textShadow: '0 1px 2px rgba(0,0,0,0.15)',
          }}
        >
          <span className="relative z-10 flex items-center gap-3">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(130,220,255,0.7)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 20h9"/>
              <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/>
            </svg>
            书写你的旅行故事
          </span>
          <div
            className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            style={{ background: 'rgba(30,55,85,0.6)' }}
          />
        </button>

        {/* 装饰点 */}
        <div className="mt-14 mb-8 flex items-center justify-center gap-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="rounded-full"
              style={{
                width: (2 + i * 0.5) + 'px',
                height: (2 + i * 0.5) + 'px',
                background: i === 2 ? 'rgba(255,230,150,0.4)' : 'rgba(130,200,255,0.25)',
                animation: `twinkle ${3 + i * 0.5}s ease-in-out ${i * 0.3}s infinite`,
              }}
            />
          ))}
        </div>

        {/* 品牌签名 */}
        <p
          className="text-xs tracking-widest"
          style={{ color: 'rgba(100,140,170,0.3)' }}
        >
          SIMON'S CAT TRAVEL &middot; 西蒙的猫旅行日记 &copy; 2026
        </p>
      </div>

      {/* 底部：男孩+猫+灯塔场景 */}
      <div className="relative z-10 w-full">
        <div
          className="relative overflow-hidden"
          style={{ maxHeight: '320px', minHeight: '180px' }}
        >
          <img
            src="/bg-ending-boy-cat.png"
            alt="男孩和猫看向灯塔"
            className="w-full h-auto"
            style={{
              objectFit: 'cover',
              objectPosition: 'center bottom',
              maxHeight: '320px',
              filter: 'drop-shadow(0 0 30px rgba(88,183,232,0.2))',
            }}
          />
          <div
            className="absolute inset-x-0 top-0 h-20 pointer-events-none"
            style={{ background: 'linear-gradient(180deg, #020810 0%, transparent 100%)' }}
          />
        </div>
      </div>

      {/* 自适应动画 */}
      <style>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0.2; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.3); }
        }
        @keyframes endingBreathe {
          0%, 100% { opacity: 1; textShadow: 0 0 40px rgba(88,183,232,0.2), 0 0 80px rgba(88,183,232,0.08); }
          50% { opacity: 0.94; textShadow: 0 0 60px rgba(88,183,232,0.3), 0 0 120px rgba(88,183,232,0.12); }
        }
        @media (prefers-reduced-motion: reduce) {
          * { animation: none !important; }
        }
      `}</style>
    </section>
  );
};

export default EndingSection;
